import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import { useTreeViewStyles } from 'components/WorkspaceEditor/styles'
import React, { memo, useCallback } from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { useKeyModificationDialogStore } from '../../KeyModificationWarningDialog'

interface KeyedTextProps {
    /** The keyed text value (typically a label followed by colon) */
    keyedText: string
    /** The class name to apply styling */
    classes?: string
    /** Optional test id for testing */
    'data-testid'?: string
    /** Whether this is a Product Type key (non-editable) */
    isPTKey?: boolean
}

/**
 * A component that renders a keyed text.
 * For PT keys, shows a warning dialog on click.
 * @param {KeyedTextProps} props - Props for the component.
 */
export const KeyedText = memo<KeyedTextProps>(
    ({ keyedText, classes, 'data-testid': dataTestId, isPTKey = false }) => {
        const { readOnlyCursor } = useTreeViewStyles()
        const { previewFeatures } = usePreviewFeatures()
        const { showDialog, closeDialog } = useKeyModificationDialogStore(
            (state) => ({
                showDialog: state.showDialog,
                closeDialog: state.closeDialog,
            })
        )

        const handleClick = useCallback(
            (event: React.MouseEvent) => {
                // Skip if feature flag is disabled
                if (!previewFeatures.hasKeyModification) return

                if (isPTKey) {
                    // Prevent any default behavior
                    event.preventDefault()
                    event.stopPropagation()

                    // Show blocking warning for PT keys
                    showDialog({
                        isPTKey: true,
                        originalKey: keyedText,
                        modifiedKey: keyedText,
                        onProceed: closeDialog,
                        onCancel: closeDialog,
                    })
                }
            },
            [isPTKey, keyedText, showDialog, closeDialog, previewFeatures.hasKeyModification]
        )

        return (
            <Typography
                variant="caption"
                className={clsx(classes, readOnlyCursor)}
                data-testid={dataTestId || 'keyed-text'}
                onClick={handleClick}
                style={isPTKey ? { cursor: 'default' } : undefined}
            >
                {keyedText}
            </Typography>
        )
    }
)
